import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import { MagnifyingGlass, MapPin, Copy, Spinner, Calculator, Tire, X, CaretDown, CaretUp, Trash, Plus, PencilSimple } from '@phosphor-icons/react';
import StockModal from './StockModal';
import TimeEstimationPanel from './TimeEstimationPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Tire dimension options
const WIDTHS = [155, 165, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295, 305, 315, 325, 335];
const HEIGHTS = [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80];
const DIAMETERS = [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

const delayOptions = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1h' },
  { value: 75, label: '1h15' },
  { value: 90, label: '1h30' },
  { value: 105, label: '1h45' },
  { value: 120, label: '2h' },
  { value: 135, label: '2h15' },
  { value: 150, label: '2h30' },
  { value: 165, label: '2h45' },
  { value: 180, label: '3h' },
];

const interventionTypes = [
  'Remplacement pneus',
  'Réparation crevaison'
];

const tireTypes = ['Neuf', 'Occasion'];

export default function InterventionForm({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [autoBreakdown, setAutoBreakdown] = useState([]);
  const [priceOverrides, setPriceOverrides] = useState({});  // { [key]: { amount?, multiplier? } }
  const [customLines, setCustomLines] = useState([]);        // [{ id, label, amount }]
  const [deletedKeys, setDeletedKeys] = useState(new Set()); // Set<string>
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);
  const [newCustomLabel, setNewCustomLabel] = useState('');
  const [newCustomAmount, setNewCustomAmount] = useState('');
  const [tempId, setTempId] = useState(null);
  const [geolocSmsOpen, setGeolocSmsOpen] = useState(false);
  const [geolocSmsPhone, setGeolocSmsPhone] = useState('');
  const [geolocSmsSending, setGeolocSmsSending] = useState(false);
  const [geolocShortUrl, setGeolocShortUrl] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [estimation, setEstimation] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    intervention_type: '',
    tire_width: '',
    tire_height: '',
    tire_diameter: '',
    tire_type: '',
    tire_brand: '',
    tire_model: '',
    condition_score: '',
    selected_stock_id: '',
    selected_stock_quantity: 1,
    tire_quantity: 2,
    address: '',
    price: '',
    delay_minutes: 60,
    delay_option: 'urgence_8h',
    is_high_season: false,
    is_rare_size: false,
    is_single_tire: false,
    is_premium: false,
    client_comes_himself: false,
    travel_time_minutes: 30,
    notes: '',
    vehicle_plate: '',
    vehicle_brand: '',
    vehicle_model: '',
    tire_source: '',
    assigned_technician_id: '',
    assigned_technician_name: '',
    onsite_duration: null,
    estimated_departure: '',
    estimated_arrival: '',
    estimated_end: '',
    travel_leg1_minutes: null,
    travel_leg2_minutes: null
  });

  const [announcedTime, setAnnouncedTime] = useState('');
  const [geolocLink, setGeolocLink] = useState('');

  // Fetch pricing config
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/api/pricing/config`, {
          headers: getAuthHeaders()
        });
        setPricingConfig(response.data);
      } catch (err) {
        console.error('Failed to fetch pricing config');
      }
    };
    if (isOpen) {
      fetchPricing();
      // Fetch technicians
      const fetchTechs = async () => {
        try {
          const r = await axios.get(`${API}/api/users`, { headers: getAuthHeaders() });
          const techs = r.data.filter(u => u.role === 'technician' && u.active);
          setTechnicians(techs);
          // Auto-select if only one
          if (techs.length === 1 && !formData.assigned_technician_id) {
            setFormData(prev => ({ ...prev, assigned_technician_id: techs[0].id, assigned_technician_name: techs[0].name }));
          }
        } catch {}
      };
      fetchTechs();
    }
  }, [isOpen]);

  // Calculate price based on formula — produces stable-keyed auto breakdown
  const calculatePrice = useCallback(() => {
    if (!pricingConfig || !formData.intervention_type) return;

    const breakdown = [];
    const isRepair = formData.intervention_type === 'Réparation crevaison';
    const qty = parseInt(formData.tire_quantity) || 2;
    const travelLabelFor = (t) => t <= 30 ? '<30min' : t <= 45 ? '31-45min' : t <= 75 ? '46-75min' : '76-120min';
    const diameterLabelFor = (d) => (d >= 17 && d <= 18) ? '17-18' : (d >= 19 && d <= 20) ? '19-20' : (d === 21) ? '21' : (d >= 22) ? '22-23' : null;
    const diameter = parseInt(formData.tire_diameter) || 0;
    const diaLabel = diameterLabelFor(diameter);

    if (isRepair) {
      const base = pricingConfig.repair_base_price || 0;
      if (base) breakdown.push({ key: 'repair_base', label: 'Prix de base réparation', amount: base, type: 'auto' });

      if (!formData.client_comes_himself) {
        const travel = formData.travel_time_minutes || 30;
        let travelCost = 0;
        if (travel <= 30) travelCost = pricingConfig.repair_travel_under_30 || 0;
        else if (travel <= 45) travelCost = pricingConfig.repair_travel_31_45 || 0;
        else if (travel <= 75) travelCost = pricingConfig.repair_travel_46_75 || 0;
        else travelCost = pricingConfig.repair_travel_76_120 || 0;
        if (travelCost) breakdown.push({ key: 'travel', label: `Tarif déplacement (${travelLabelFor(travel)})`, amount: travelCost, type: 'auto' });
      }

      if (formData.delay_option === 'urgence_2h') {
        const v = pricingConfig.repair_urgence_2h || 0;
        if (v) breakdown.push({ key: 'urgence', label: 'Urgence 2h', amount: v, type: 'auto' });
      } else if (formData.delay_option === 'urgence_8h') {
        const v = pricingConfig.repair_urgence_8h || 0;
        if (v) breakdown.push({ key: 'urgence', label: 'Urgence 8h', amount: v, type: 'auto' });
      } else if (formData.delay_option === 'autre_jour') {
        const v = pricingConfig.repair_autre_jour || 0;
        if (v) breakdown.push({ key: 'autre_jour', label: 'Autre jour', amount: v, type: 'auto' });
      }

      if (formData.is_high_season) {
        const v = pricingConfig.repair_high_season || 0;
        if (v) breakdown.push({ key: 'high_season', label: 'Haute saison', amount: v, type: 'auto' });
      }

    } else {
      const base = pricingConfig.replace_base_price || 0;
      if (base) breakdown.push({ key: 'base', label: 'Prix de base pneus', amount: base, type: 'auto' });

      let diaSurcharge = 0;
      if (diameter >= 17 && diameter <= 18) diaSurcharge = pricingConfig.replace_diameter_17_18 || 0;
      else if (diameter >= 19 && diameter <= 20) diaSurcharge = pricingConfig.replace_diameter_19_20 || 0;
      else if (diameter === 21) diaSurcharge = pricingConfig.replace_diameter_21 || 0;
      else if (diameter >= 22) diaSurcharge = pricingConfig.replace_diameter_22_23 || 0;
      if (diaSurcharge && diaLabel) {
        breakdown.push({ key: 'diameter_surcharge', label: `Surtaxe diamètre ${diaLabel}`, amount: diaSurcharge, type: 'auto' });
      }

      if (formData.condition_score) {
        const score = parseInt(formData.condition_score);
        let mult = 1;
        if (score === 1) mult = pricingConfig.replace_condition_1_multiplier || 1;
        else if (score === 2) mult = pricingConfig.replace_condition_2_multiplier || 1;
        else if (score === 3) mult = pricingConfig.replace_condition_3_multiplier || 1;
        else if (score === 4) mult = pricingConfig.replace_condition_4_multiplier || 1;
        if (mult !== 1) breakdown.push({ key: 'mult_condition', label: `Multiplicateur état ${score}/4`, multiplier: mult, type: 'multiplier' });
      }

      if (formData.tire_type === 'Neuf') {
        const mult = pricingConfig.replace_type_neuf_multiplier || 1.7;
        if (mult !== 1) breakdown.push({ key: 'mult_neuf', label: 'Multiplicateur neuf', multiplier: mult, type: 'multiplier' });
      }

      if (formData.is_premium) {
        const mult = pricingConfig.replace_premium_multiplier || 1.2;
        if (mult !== 1) breakdown.push({ key: 'mult_premium', label: 'Multiplicateur Premium', multiplier: mult, type: 'multiplier' });
      }

      if (formData.is_rare_size) {
        const mult = pricingConfig.replace_rare_size_multiplier || 1;
        if (mult !== 1) breakdown.push({ key: 'mult_rare', label: 'Taille rare', multiplier: mult, type: 'multiplier' });
      }

      // qty 1/2 → ×1 ; qty 3/4 → ×2
      const qtyMultiplier = Math.ceil(qty / 2);
      if (qtyMultiplier > 1) {
        breakdown.push({ key: 'mult_qty', label: `Quantité (${qty} pneus)`, multiplier: qtyMultiplier, type: 'multiplier' });
      }

      breakdown.push({ key: 'subtotal_tires', label: 'Sous-total pneus', amount: 0, type: 'subtotal' });

      const montageBase = pricingConfig.replace_montage_base || 0;
      if (montageBase) breakdown.push({ key: 'montage_base', label: 'Montage base', amount: montageBase, type: 'auto' });

      let montageDia = 0;
      if (diameter >= 17 && diameter <= 18) montageDia = pricingConfig.replace_montage_17_18 || 0;
      else if (diameter >= 19 && diameter <= 20) montageDia = pricingConfig.replace_montage_19_20 || 0;
      else if (diameter === 21) montageDia = pricingConfig.replace_montage_21 || 0;
      else if (diameter >= 22) montageDia = pricingConfig.replace_montage_22_23 || 0;
      if (montageDia && diaLabel) breakdown.push({ key: 'montage_dia', label: `Surtaxe montage ${diaLabel}`, amount: montageDia, type: 'auto' });

      if (!formData.client_comes_himself) {
        const travel = formData.travel_time_minutes || 30;
        let travelCost = 0;
        if (travel <= 30) travelCost = pricingConfig.replace_travel_under_30 || 0;
        else if (travel <= 45) travelCost = pricingConfig.replace_travel_31_45 || 0;
        else if (travel <= 75) travelCost = pricingConfig.replace_travel_46_75 || 0;
        else travelCost = pricingConfig.replace_travel_76_120 || 0;
        if (travelCost) breakdown.push({ key: 'travel', label: `Tarif déplacement (${travelLabelFor(travel)})`, amount: travelCost, type: 'auto' });
      }

      if (formData.delay_option === 'urgence_2h') {
        const v = pricingConfig.replace_urgence_2h || 0;
        if (v) breakdown.push({ key: 'urgence', label: 'Urgence 2h', amount: v, type: 'auto' });
      } else if (formData.delay_option === 'urgence_8h') {
        const v = pricingConfig.replace_urgence_8h || 0;
        if (v) breakdown.push({ key: 'urgence', label: 'Urgence 8h', amount: v, type: 'auto' });
      }

      if (formData.delay_option === 'autre_jour') {
        const v = pricingConfig.replace_autre_jour || 0;
        if (v) breakdown.push({ key: 'autre_jour', label: 'Autre jour', amount: v, type: 'auto' });
      }

      if (formData.is_high_season) {
        const v = pricingConfig.replace_high_season || 0;
        if (v) breakdown.push({ key: 'high_season', label: 'Haute saison', amount: v, type: 'auto' });
      }

      if (qty === 1 || qty === 3) {
        const v = pricingConfig.replace_single_tire_discount || 0;
        if (v) breakdown.push({ key: 'remise', label: 'Remise 1 pneu', amount: v, type: 'auto' });
      }
    }

    setAutoBreakdown(breakdown);
  }, [pricingConfig, formData]);

  // Recalculate price when relevant fields change
  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  // Compute effective lines from autoBreakdown + overrides + deletedKeys + customLines
  const { effectiveLines, computedTotal } = (() => {
    const lines = [];
    let running = 0;
    for (const line of autoBreakdown) {
      if (deletedKeys.has(line.key)) continue;
      const override = priceOverrides[line.key];
      const newLine = { ...line };
      if (line.type === 'multiplier') {
        if (override?.multiplier !== undefined) newLine.multiplier = override.multiplier;
        running *= newLine.multiplier;
        newLine.amount = 0;
      } else if (line.type === 'subtotal') {
        newLine.amount = Math.round(running * 100) / 100;
      } else {
        if (override?.amount !== undefined) newLine.amount = override.amount;
        running += (newLine.amount || 0);
      }
      lines.push(newLine);
    }
    for (const c of customLines) {
      const v = Number(c.amount) || 0;
      running += v;
      lines.push({ key: `custom_${c.id}`, label: c.label, amount: v, type: 'custom' });
    }
    const finalTotal = Math.max(0, Math.round(running * 100) / 100);
    lines.push({ key: 'total', label: 'Total calculé', amount: finalTotal, type: 'total' });
    return { effectiveLines: lines, computedTotal: finalTotal };
  })();

  // Sync calculatedPrice with computedTotal
  useEffect(() => {
    setCalculatedPrice(computedTotal);
  }, [computedTotal]);

  // Breakdown editing handlers
  const startEditAmount = (line) => {
    setEditingKey(line.key);
    if (line.type === 'multiplier') {
      setEditingValue(String(line.multiplier));
    } else {
      setEditingValue(String(line.amount));
    }
  };
  const commitEdit = (line) => {
    const n = parseFloat(editingValue);
    if (!Number.isNaN(n)) {
      if (line.type === 'custom') {
        setCustomLines(prev => prev.map(c => `custom_${c.id}` === line.key ? { ...c, amount: n } : c));
      } else if (line.type === 'multiplier') {
        setPriceOverrides(prev => ({ ...prev, [line.key]: { ...(prev[line.key] || {}), multiplier: n } }));
      } else {
        setPriceOverrides(prev => ({ ...prev, [line.key]: { ...(prev[line.key] || {}), amount: n } }));
      }
    }
    setEditingKey(null);
    setEditingValue('');
  };
  const deleteLine = (line) => {
    if (line.type === 'custom') {
      setCustomLines(prev => prev.filter(c => `custom_${c.id}` !== line.key));
    } else {
      setDeletedKeys(prev => new Set([...prev, line.key]));
    }
  };
  const addCustomLine = () => {
    const label = newCustomLabel.trim();
    const amount = parseFloat(newCustomAmount);
    if (!label || Number.isNaN(amount)) return;
    setCustomLines(prev => [...prev, { id: Date.now() + Math.random(), label, amount }]);
    setNewCustomLabel('');
    setNewCustomAmount('');
    setAddingCustom(false);
  };
  const resetBreakdown = () => {
    setPriceOverrides({});
    setCustomLines([]);
    setDeletedKeys(new Set());
  };

  // Auto-fill price when calculated
  useEffect(() => {
    if (calculatedPrice > 0 && !formData.price) {
      setFormData(prev => ({ ...prev, price: calculatedPrice.toString() }));
    }
  }, [calculatedPrice]);

  useEffect(() => {
    if (formData.delay_minutes) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + formData.delay_minutes);
      setAnnouncedTime(now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
    }
  }, [formData.delay_minutes]);

  // Auto-fetch stock when all 3 dimensions are selected
  useEffect(() => {
    if (formData.tire_width && formData.tire_height && formData.tire_diameter) {
      setStockModalOpen(true);
    }
  }, [formData.tire_width, formData.tire_height, formData.tire_diameter]);

  const handlePhoneBlur = async () => {
    if (!formData.client_phone || formData.client_phone.length < 10) return;
    
    setLookingUp(true);
    try {
      const response = await axios.post(
        `${API}/api/clients/lookup`,
        { phone: formData.client_phone },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.found) {
        const client = response.data.client;
        toast('Client connu', {
          description: 'Voulez-vous pré-remplir les informations ?',
          action: {
            label: 'Pré-remplir',
            onClick: () => {
              setFormData(prev => ({
                ...prev,
                client_name: client.name || prev.client_name,
                client_email: client.email || prev.client_email,
                tire_width: client.last_width || prev.tire_width,
                tire_height: client.last_height || prev.tire_height,
                tire_diameter: client.last_diameter || prev.tire_diameter,
                tire_type: client.last_type || prev.tire_type,
                tire_brand: client.last_brand || prev.tire_brand
              }));
            }
          }
        });
      }
    } catch (err) {
      // Ignore lookup errors
    } finally {
      setLookingUp(false);
    }
  };

  const handleStockSelect = (stockItem) => {
    setFormData(prev => ({
      ...prev,
      tire_type: stockItem.type === 'neuf' ? 'Neuf' : 'Occasion',
      tire_brand: stockItem.brand,
      tire_model: stockItem.model,
      condition_score: stockItem.condition_score || '',
      selected_stock_id: stockItem.id || '',
      tire_source: 'Stock Reco Pneus'
    }));
    setStockModalOpen(false);
    toast.success('Pneu sélectionné depuis le stock');
  };

  const handleExternalSelect = (source) => {
    setFormData(prev => ({
      ...prev,
      selected_stock_id: '',
      tire_source: source
    }));
    setStockModalOpen(false);
    toast.success('Source externe sélectionnée');
  };

  const handleEstimationChange = useCallback((est) => {
    setEstimation(est);
    setFormData(prev => ({
      ...prev,
      travel_time_minutes: est.rounded_travel_minutes || prev.travel_time_minutes,
      onsite_duration: est.onsite_duration,
      estimated_departure: est.estimated_departure,
      estimated_arrival: est.estimated_arrival,
      estimated_end: est.estimated_end,
      travel_leg1_minutes: est.travel_leg1_minutes,
      travel_leg2_minutes: est.travel_leg2_minutes,
      delay_minutes: est.rounded_travel_minutes + est.onsite_duration,
      price: '' // Trigger recalculation
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: price_to_collect must match the sum of price_lines.
    // computedTotal is the authoritative breakdown total (recomputed on every change).
    const priceToCollect = parseFloat(formData.price);
    if (!Number.isNaN(priceToCollect) && priceToCollect > 0 && effectiveLines.length > 0) {
      if (Math.abs(priceToCollect - computedTotal) >= 0.5) {
        toast.error('Erreur : le total des lignes ne correspond pas au prix à encaisser. Veuillez recliquer sur Appliquer.');
        return;
      }
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/interventions`,
        {
          ...formData,
          tire_width: formData.tire_width ? parseInt(formData.tire_width) : null,
          tire_height: formData.tire_height ? parseInt(formData.tire_height) : null,
          tire_diameter: formData.tire_diameter ? parseInt(formData.tire_diameter) : null,
          condition_score: formData.condition_score ? parseInt(formData.condition_score) : null,
          price: parseFloat(formData.price),
          delay_minutes: parseInt(formData.delay_minutes),
          travel_time_minutes: parseInt(formData.travel_time_minutes) || null,
          selected_stock_quantity: parseInt(formData.tire_quantity) || 1,
          is_single_tire: parseInt(formData.tire_quantity) === 1 || parseInt(formData.tire_quantity) === 3,
          temp_id: tempId || undefined,
          tire_source: formData.tire_source || undefined,
          assigned_technician_id: formData.assigned_technician_id || undefined,
          assigned_technician_name: formData.assigned_technician_name || undefined,
          onsite_duration: formData.onsite_duration || undefined,
          estimated_departure: formData.estimated_departure || undefined,
          estimated_arrival: formData.estimated_arrival || undefined,
          estimated_end: formData.estimated_end || undefined,
          travel_leg1_minutes: formData.travel_leg1_minutes,
          travel_leg2_minutes: formData.travel_leg2_minutes,
          is_premium: !!formData.is_premium,
          price_lines: effectiveLines.map(({ key, ...rest }) => rest)
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Intervention créée avec succès');
      
      // Generate geoloc link
      const frontendUrl = window.location.origin;
      setGeolocLink(`${frontendUrl}/locate/${response.data.geoloc_token}`);
      
      onSuccess?.(response.data);
      
      // Reset form
      setTempId(null);
      setGeolocSmsOpen(false);
      setGeolocShortUrl('');
      setFormData({
        client_name: '',
        client_phone: '',
        client_email: '',
        intervention_type: '',
        tire_width: '',
        tire_height: '',
        tire_diameter: '',
        tire_type: '',
        tire_brand: '',
        tire_model: '',
        condition_score: '',
        selected_stock_id: '',
        selected_stock_quantity: 1,
        tire_quantity: 2,
        address: '',
        price: '',
        delay_minutes: 60,
        delay_option: 'urgence_8h',
        is_high_season: false,
        is_rare_size: false,
        is_single_tire: false,
        is_premium: false,
        client_comes_himself: false,
        travel_time_minutes: 30,
        notes: '',
        vehicle_plate: '',
        vehicle_brand: '',
        vehicle_model: '',
        tire_source: '',
        assigned_technician_id: '',
        assigned_technician_name: '',
        onsite_duration: null,
        estimated_departure: '',
        estimated_arrival: '',
        estimated_end: '',
        travel_leg1_minutes: null,
        travel_leg2_minutes: null
      });
      // Reset editable breakdown state
      resetBreakdown();
      
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Erreur lors de la création';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    // Delete temp record if form is cancelled
    if (tempId) {
      try {
        await axios.delete(`${API}/api/interventions/${tempId}`, { headers: getAuthHeaders() });
      } catch {}
      setTempId(null);
    }
    setGeolocSmsOpen(false);
    setGeolocShortUrl('');
    onClose();
  };

  const applyCalculatedPrice = () => {
    setFormData(prev => ({ ...prev, price: calculatedPrice.toFixed(2) }));
  };

  const dimension = formData.tire_width && formData.tire_height && formData.tire_diameter 
    ? `${formData.tire_width}/${formData.tire_height}R${formData.tire_diameter}`
    : null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-gray-900">Nouvelle intervention</DialogTitle>
            <DialogDescription className="text-gray-500">
              Remplissez les informations de l'intervention
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Client Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Nom du client *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData(p => ({ ...p, client_name: e.target.value }))}
                  className="bg-white border-gray-300 text-gray-900"
                  required
                  data-testid="form-client-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Téléphone *</Label>
                <div className="relative">
                  <Input
                    value={formData.client_phone}
                    onChange={(e) => setFormData(p => ({ ...p, client_phone: e.target.value }))}
                    onBlur={handlePhoneBlur}
                    className="bg-white border-gray-300 text-gray-900 pr-10"
                    required
                    data-testid="form-client-phone"
                  />
                  {lookingUp && (
                    <Spinner size={18} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Email (optionnel)</Label>
              <Input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(p => ({ ...p, client_email: e.target.value }))}
                className="bg-white border-gray-300 text-gray-900"
                data-testid="form-client-email"
              />
            </div>

            {/* Intervention Type */}
            <div className="space-y-2">
              <Label className="text-gray-700">Type d'intervention *</Label>
              <Select
                value={formData.intervention_type}
                onValueChange={(v) => setFormData(p => ({ ...p, intervention_type: v, price: '' }))}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="form-intervention-type">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {interventionTypes.map(type => (
                    <SelectItem key={type} value={type} className="text-gray-900 hover:bg-gray-100">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Technician Assignment */}
            <div className="space-y-2">
              <Label className="text-gray-700">Technicien assigné</Label>
              <Select
                value={formData.assigned_technician_id}
                onValueChange={(v) => {
                  const tech = technicians.find(t => t.id === v);
                  setFormData(p => ({ ...p, assigned_technician_id: v, assigned_technician_name: tech?.name || '' }));
                }}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="form-technician">
                  <SelectValue placeholder="Sélectionner un technicien" />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tire Dimensions - 3 Separate Dropdowns */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Tire size={20} className="text-reco-500" />
                <Label className="text-gray-700 font-semibold">Dimension des pneus</Label>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Largeur</Label>
                  <Select
                    value={formData.tire_width?.toString() || ''}
                    onValueChange={(v) => setFormData(p => ({ ...p, tire_width: v }))}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="form-tire-width">
                      <SelectValue placeholder="Largeur" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-60">
                      {WIDTHS.map(w => (
                        <SelectItem key={w} value={w.toString()} className="text-gray-900">{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Hauteur</Label>
                  <Select
                    value={formData.tire_height?.toString() || ''}
                    onValueChange={(v) => setFormData(p => ({ ...p, tire_height: v }))}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="form-tire-height">
                      <SelectValue placeholder="Hauteur" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-60">
                      {HEIGHTS.map(h => (
                        <SelectItem key={h} value={h.toString()} className="text-gray-900">{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Diamètre</Label>
                  <Select
                    value={formData.tire_diameter?.toString() || ''}
                    onValueChange={(v) => setFormData(p => ({ ...p, tire_diameter: v }))}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="form-tire-diameter">
                      <SelectValue placeholder="Diamètre" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-60">
                      {DIAMETERS.map(d => (
                        <SelectItem key={d} value={d.toString()} className="text-gray-900">R{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {dimension && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-gray-600">Dimension: <strong>{dimension}</strong></span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setStockModalOpen(true)}
                    className="border-reco-500 text-reco-500 hover:bg-reco-50"
                    data-testid="form-check-stock"
                  >
                    <MagnifyingGlass size={16} className="mr-1" />
                    Voir stock
                  </Button>
                </div>
              )}
            </div>

            {/* Tire Details */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Quantité *</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.tire_quantity}
                  onChange={(e) => setFormData(p => ({ ...p, tire_quantity: parseInt(e.target.value) || 1, price: '' }))}
                  className="bg-white border-gray-300 text-gray-900 font-semibold"
                  data-testid="form-tire-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Type pneus</Label>
                <Select
                  value={formData.tire_type}
                  onValueChange={(v) => setFormData(p => ({ ...p, tire_type: v, price: '' }))}
                >
                  <SelectTrigger className="bg-white border-gray-300" data-testid="form-tire-type">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {tireTypes.map(type => (
                      <SelectItem key={type} value={type} className="text-gray-900">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Marque pneus</Label>
                <Input
                  value={formData.tire_brand}
                  onChange={(e) => setFormData(p => ({ ...p, tire_brand: e.target.value }))}
                  placeholder="Michelin..."
                  className="bg-white border-gray-300"
                  data-testid="form-tire-brand"
                />
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="is_premium"
                    checked={formData.is_premium}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, is_premium: !!c, price: '' }))}
                    data-testid="form-is-premium"
                  />
                  <label htmlFor="is_premium" className="text-sm text-gray-700 cursor-pointer select-none">
                    Pneus Premium
                  </label>
                </div>
              </div>

              {formData.tire_type === 'Occasion' && (
                <div className="space-y-2">
                  <Label className="text-gray-700">État (1-4)</Label>
                  <Select
                    value={formData.condition_score?.toString() || ''}
                    onValueChange={(v) => setFormData(p => ({ ...p, condition_score: v, price: '' }))}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="form-condition-score">
                      <SelectValue placeholder="Score..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="1" className="text-gray-900">1 - Usé</SelectItem>
                      <SelectItem value="2" className="text-gray-900">2 - Correct</SelectItem>
                      <SelectItem value="3" className="text-gray-900">3 - Bon</SelectItem>
                      <SelectItem value="4" className="text-gray-900">4 - Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="text-gray-700">Adresse exacte *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                className="bg-white border-gray-300"
                required
                data-testid="form-address"
              />
              {/* Geoloc SMS Button */}
              <div className="mt-2">
                {!geolocSmsOpen ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (!formData.client_phone) {
                        toast.error('Veuillez saisir un numéro de téléphone d\'abord');
                        return;
                      }
                      setGeolocSmsPhone(formData.client_phone);
                      setGeolocSmsOpen(true);
                    }}
                    className="w-full border-gray-300 hover:bg-gray-100 gap-2 text-sm"
                    data-testid="form-geoloc-sms-btn"
                  >
                    <MapPin size={16} className="text-reco-500" />
                    Envoyer lien de position par SMS
                  </Button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input
                      value={geolocSmsPhone}
                      onChange={(e) => setGeolocSmsPhone(e.target.value)}
                      placeholder="+33..."
                      className="bg-white border-gray-300 flex-1"
                    />
                    <Button
                      type="button"
                      onClick={async () => {
                        setGeolocSmsSending(true);
                        try {
                          const res = await axios.post(`${API}/api/interventions/temp-geoloc`, {
                            phone: geolocSmsPhone,
                            client_name: formData.client_name,
                            frontend_url: window.location.origin
                          }, { headers: getAuthHeaders() });
                          setTempId(res.data.intervention_id);
                          const shortUrl = `${window.location.origin}/r/${res.data.geoloc_short_code}`;
                          setGeolocShortUrl(shortUrl);
                          if (res.data.sms_sent) {
                            toast.success('SMS de géolocalisation envoyé');
                          } else {
                            toast.warning('SMS non envoyé — Twilio non configuré');
                          }
                          setGeolocSmsOpen(false);
                          // Start polling for address
                          const pollInterval = setInterval(async () => {
                            try {
                              const r = await axios.get(`${API}/api/interventions/${res.data.intervention_id}/geoloc-address`, { headers: getAuthHeaders() });
                              if (r.data.address) {
                                setFormData(p => ({ ...p, address: r.data.address }));
                                clearInterval(pollInterval);
                                toast.success('Adresse du client reçue !');
                              }
                            } catch {}
                          }, 5000);
                          // Stop polling after 10 minutes
                          setTimeout(() => clearInterval(pollInterval), 600000);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Erreur');
                        } finally {
                          setGeolocSmsSending(false);
                        }
                      }}
                      disabled={geolocSmsSending}
                      className="bg-reco-500 hover:bg-reco-600 text-white"
                    >
                      {geolocSmsSending ? '...' : 'Envoyer'}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setGeolocSmsOpen(false)} className="text-gray-400">
                      <X size={16} />
                    </Button>
                  </div>
                )}
                {tempId && (
                  <div className="space-y-2 mt-2">
                    <p className="text-xs text-reco-600">SMS envoyé — en attente de la position du client...</p>
                    {geolocShortUrl && (
                      <div className="flex items-center gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <MapPin size={16} className="text-reco-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate flex-1 font-mono">{geolocShortUrl}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { navigator.clipboard.writeText(geolocShortUrl); toast.success('Lien copié !'); }}
                          className="text-reco-500 hover:text-reco-600 flex-shrink-0"
                          data-testid="copy-geoloc-link"
                        >
                          <Copy size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Price Calculator Section */}
            <div className="p-4 bg-reco-50 rounded-lg border border-reco-200">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={20} className="text-reco-600" />
                <Label className="text-reco-700 font-semibold">Calcul du prix</Label>
              </div>

              {/* Travel Options */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="client_comes"
                    checked={formData.client_comes_himself}
                    onCheckedChange={(checked) => setFormData(p => ({ ...p, client_comes_himself: checked, price: '' }))}
                  />
                  <label htmlFor="client_comes" className="text-sm text-gray-700">
                    Le client se déplace lui-même
                  </label>
                </div>

                {!formData.client_comes_himself && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">Temps de trajet estimé (minutes)</Label>
                    <Select
                      value={formData.travel_time_minutes?.toString() || '30'}
                      onValueChange={(v) => setFormData(p => ({ ...p, travel_time_minutes: parseInt(v), price: '' }))}
                    >
                      <SelectTrigger className="bg-white border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="15">Moins de 30 min</SelectItem>
                        <SelectItem value="35">31-45 min</SelectItem>
                        <SelectItem value="60">46 min - 1h15</SelectItem>
                        <SelectItem value="90">1h16 - 2h</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Delay Options */}
              <div className="space-y-2 mb-4">
                <Label className="text-sm text-gray-600">Délai d'intervention</Label>
                <RadioGroup
                  value={formData.delay_option}
                  onValueChange={(v) => setFormData(p => ({ ...p, delay_option: v, price: '' }))}
                  className="flex flex-wrap gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="urgence_2h" id="urgence_2h" />
                    <label htmlFor="urgence_2h" className="text-sm">Urgence 2h</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="urgence_8h" id="urgence_8h" />
                    <label htmlFor="urgence_8h" className="text-sm">Urgence 8h</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="autre_jour" id="autre_jour" />
                    <label htmlFor="autre_jour" className="text-sm">Autre jour</label>
                  </div>
                </RadioGroup>
              </div>

              {/* Additional Options */}
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="high_season"
                    checked={formData.is_high_season}
                    onCheckedChange={(checked) => setFormData(p => ({ ...p, is_high_season: checked, price: '' }))}
                  />
                  <label htmlFor="high_season" className="text-sm text-gray-700">Haute saison</label>
                </div>

                {formData.intervention_type === 'Remplacement pneus' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="rare_size"
                        checked={formData.is_rare_size}
                        onCheckedChange={(checked) => setFormData(p => ({ ...p, is_rare_size: checked, price: '' }))}
                      />
                      <label htmlFor="rare_size" className="text-sm text-gray-700">Taille rare</label>
                    </div>
                    {(parseInt(formData.tire_quantity) === 1 || parseInt(formData.tire_quantity) === 3) && (
                      <div className="flex items-center space-x-2 text-sm text-reco-600 font-medium">
                        <span>Remise 1 pneu (auto)</span>
                      </div>
                    )}
                    {(parseInt(formData.tire_quantity) >= 3) && (
                      <div className="flex items-center space-x-2 text-sm text-blue-600 font-medium">
                        <span>Base diamètre x2 (auto)</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Calculated Price Display */}
              {calculatedPrice > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-white rounded border border-reco-300">
                    <span className="text-gray-700">Prix calculé:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-reco-600" data-testid="calculated-price">{calculatedPrice.toFixed(2)} €</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={applyCalculatedPrice}
                        className="border-reco-500 text-reco-600 hover:bg-reco-50"
                      >
                        Appliquer
                      </Button>
                    </div>
                  </div>

                  {/* Collapsible editable breakdown */}
                  {effectiveLines.length > 0 && (
                    <div className="bg-white rounded border border-gray-200">
                      <button
                        type="button"
                        onClick={() => setBreakdownOpen(!breakdownOpen)}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-reco-600 hover:bg-gray-50 transition-colors"
                        data-testid="toggle-price-breakdown"
                      >
                        <span className="flex items-center gap-1.5">
                          {breakdownOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
                          {breakdownOpen ? 'Masquer le détail du calcul' : 'Voir le détail du calcul'}
                        </span>
                        <span className="text-gray-400">{effectiveLines.length - 1} lignes</span>
                      </button>
                      {breakdownOpen && (
                        <div className="border-t border-gray-100 px-3 py-2.5 space-y-1" data-testid="price-breakdown-list">
                          {effectiveLines.map((line, i) => {
                            const isTotal = line.type === 'total';
                            const isSubtotal = line.type === 'subtotal';
                            const isMult = line.type === 'multiplier';
                            const isCustom = line.type === 'custom';
                            const editable = !isTotal && !isSubtotal;
                            const isEditing = editingKey === line.key;
                            const valueStr = isMult
                              ? `×${Number(line.multiplier).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                              : `${(line.amount ?? 0) > 0 && !isSubtotal && !isTotal ? '+' : ''}${Number(line.amount || 0).toFixed(2)} €`;

                            return (
                              <div
                                key={line.key}
                                className={`group flex items-center justify-between text-xs gap-2 ${
                                  isTotal ? 'pt-1.5 mt-1 border-t border-gray-200 font-bold text-gray-900 text-sm' :
                                  isSubtotal ? 'pt-1 mt-0.5 border-t border-gray-100 font-semibold text-gray-700' :
                                  isCustom ? 'text-blue-600 italic' :
                                  'text-gray-600'
                                }`}
                                data-testid={`breakdown-row-${i}`}
                              >
                                <span className="flex-1 truncate">{line.label}</span>

                                {isEditing && editable ? (
                                  <Input
                                    autoFocus
                                    type="number"
                                    step="0.01"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onBlur={() => commitEdit(line)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitEdit(line); }
                                      if (e.key === 'Escape') { setEditingKey(null); setEditingValue(''); }
                                    }}
                                    className="h-6 w-24 text-xs font-mono tabular-nums text-right"
                                  />
                                ) : (
                                  <span
                                    className={`font-mono tabular-nums ${editable ? 'cursor-pointer hover:bg-reco-50 px-1 rounded flex items-center gap-1' : ''} ${(line.amount || 0) < 0 && !isMult ? 'text-red-600' : ''}`}
                                    onClick={() => editable && startEditAmount(line)}
                                    data-testid={editable ? `breakdown-edit-${line.key}` : undefined}
                                    title={editable ? 'Cliquer pour modifier' : ''}
                                  >
                                    {editable && <PencilSimple size={10} className="opacity-0 group-hover:opacity-60" />}
                                    {valueStr}
                                  </span>
                                )}

                                {editable && !isEditing && (
                                  <button
                                    type="button"
                                    onClick={() => deleteLine(line)}
                                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                                    title="Supprimer"
                                    data-testid={`breakdown-delete-${line.key}`}
                                  >
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })}

                          {/* Add custom line */}
                          {addingCustom ? (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                              <Input
                                autoFocus
                                value={newCustomLabel}
                                onChange={(e) => setNewCustomLabel(e.target.value)}
                                placeholder="Libellé (ex: Supplément accès difficile)"
                                className="h-7 text-xs flex-1"
                                data-testid="new-custom-label"
                              />
                              <Input
                                type="number"
                                step="0.01"
                                value={newCustomAmount}
                                onChange={(e) => setNewCustomAmount(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLine(); } }}
                                placeholder="±€"
                                className="h-7 text-xs w-20 font-mono tabular-nums text-right"
                                data-testid="new-custom-amount"
                              />
                              <button
                                type="button"
                                onClick={addCustomLine}
                                className="text-xs text-reco-600 hover:text-reco-700 font-medium"
                                data-testid="confirm-custom-line"
                              >
                                Ajouter
                              </button>
                              <button
                                type="button"
                                onClick={() => { setAddingCustom(false); setNewCustomLabel(''); setNewCustomAmount(''); }}
                                className="text-xs text-gray-400 hover:text-gray-600"
                              >
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                              <button
                                type="button"
                                onClick={() => setAddingCustom(true)}
                                className="flex items-center gap-1 text-xs text-reco-600 hover:text-reco-700 font-medium"
                                data-testid="add-custom-line"
                              >
                                <Plus size={12} />
                                Ajouter une ligne
                              </button>
                              {(Object.keys(priceOverrides).length > 0 || customLines.length > 0 || deletedKeys.size > 0) && (
                                <button
                                  type="button"
                                  onClick={resetBreakdown}
                                  className="text-xs text-gray-400 hover:text-gray-600"
                                  data-testid="reset-breakdown"
                                >
                                  Réinitialiser
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Final Price */}
            <div className="space-y-2">
              <Label className="text-gray-700">Prix à encaisser (EUR) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))}
                className="bg-white border-gray-300 text-lg font-semibold"
                required
                data-testid="form-price"
              />
            </div>

            {/* Timing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Délai d'intervention *</Label>
                <Select
                  value={String(formData.delay_minutes)}
                  onValueChange={(v) => setFormData(p => ({ ...p, delay_minutes: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-white border-gray-300" data-testid="form-delay">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {delayOptions.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)} className="text-gray-900">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Heure théorique annoncée</Label>
                <Input
                  value={announcedTime}
                  readOnly
                  className="bg-gray-100 border-gray-300 text-reco-600 font-mono font-bold"
                  data-testid="form-announced-time"
                />
              </div>
            </div>

            {/* Source des pneus */}
            {formData.tire_source && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Source des pneus</div>
                <div className="text-sm font-medium text-gray-700" data-testid="tire-source-display">{formData.tire_source}</div>
              </div>
            )}

            {/* Time Estimation */}
            <TimeEstimationPanel
              technicianId={formData.assigned_technician_id}
              tireSource={formData.tire_source}
              clientAddress={formData.address}
              interventionType={formData.intervention_type}
              tireQuantity={formData.tire_quantity}
              onEstimationChange={handleEstimationChange}
            />

            {/* Véhicule */}
            <div className="space-y-4 pt-2">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Véhicule</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Immatriculation</Label>
                  <Input
                    value={formData.vehicle_plate}
                    onChange={(e) => setFormData(p => ({ ...p, vehicle_plate: e.target.value.toUpperCase() }))}
                    placeholder="AB-123-CD"
                    className="bg-white border-gray-300 uppercase"
                    data-testid="form-vehicle-plate"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Marque du véhicule</Label>
                  <Input
                    value={formData.vehicle_brand}
                    onChange={(e) => setFormData(p => ({ ...p, vehicle_brand: e.target.value }))}
                    placeholder="Renault, BMW..."
                    className="bg-white border-gray-300"
                    data-testid="form-vehicle-brand"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Modèle du véhicule</Label>
                  <Input
                    value={formData.vehicle_model}
                    onChange={(e) => setFormData(p => ({ ...p, vehicle_model: e.target.value }))}
                    placeholder="Clio, Série 3..."
                    className="bg-white border-gray-300"
                    data-testid="form-vehicle-model"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-gray-300 min-h-[80px]"
                data-testid="form-notes"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-gray-300 hover:bg-gray-100"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-reco-500 hover:bg-reco-600 text-white rounded-full px-6"
                data-testid="form-submit"
              >
                {loading ? 'Création...' : 'Créer l\'intervention'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <StockModal
        isOpen={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        dimension={dimension}
        onSelect={handleStockSelect}
        onExternalSelect={handleExternalSelect}
      />
    </>
  );
}
